import {
  BASE_SCALE, TILE_GUTTER, TILE_LEVELS, TILE_PX,
  createDefaultCanvas, cutRowIntoTiles, releaseCanvas, tileManifest, writeBasePicture,
} from './tileCut.js';

/**
 * Exports a PICTURE as the very pyramid `exportTrackTiles` paints: same levels, same paths, same
 * manifest (see tileCut.js), so the game cannot tell which of the two produced it.
 *
 * The picture is stretched over the map rectangle exactly as the game stretches a background image,
 * so it may be of any resolution; what it cannot be is off the map rectangle, which is what keeps
 * the tiles aligned with the track's geometry.
 *
 * One row canvas at a time is allocated, never the whole map at the densest level: the picture is
 * decoded once by the caller, and every row is read straight out of it.
 *
 * @param {object} image                the decoded picture (an ImageBitmap, or an image element)
 * @param {object} track                the track file the picture belongs to (`width`, `height`, `id`)
 * @param {object} opts
 * @param {Function} opts.encode        `(canvas, quality) => Promise<Uint8Array>`; WebP
 * @param {Function} [opts.createCanvas] `(w, h) => canvas`; defaults to document.createElement
 * @param {Function} [opts.onProgress]  `({ phase, level, index, total })`; may return a promise
 * @returns {Promise<{ manifest, files: { path: string, data: Uint8Array }[], tileCount }|null>}
 */
export async function exportImageTiles(image, track, {
  encode,
  createCanvas = createDefaultCanvas,
  onProgress = null,
} = {}) {
  if (!track || !(track?.width > 0) || !(track?.height > 0)) return null;
  if (!image || !(image.width > 0) || !(image.height > 0)) return null;
  if (typeof encode !== 'function') throw new Error('exportImageTiles: an encode(canvas) function is required');

  const id = track.id ?? 'track_custom';
  const files = [];
  const rows = TILE_LEVELS.map((scale, level) => {
    const rowWorld = TILE_PX / scale;
    return { level, scale, rowWorld, rowCount: Math.max(1, Math.ceil(track.height / rowWorld)) };
  });
  const totalRows = rows.reduce((sum, row) => sum + row.rowCount, 0);
  let done = 0;
  let tileCount = 0;

  for (const row of rows) {
    for (let rowIndex = 0; rowIndex < row.rowCount; rowIndex++) {
      await onProgress?.({ phase: 'cut', level: row.level, index: done, total: totalRows });
      const rowStartY = rowIndex * row.rowWorld;
      const canvas = createCanvas(
        Math.max(1, Math.ceil(track.width * row.scale)) + TILE_GUTTER * 2,
        TILE_PX + TILE_GUTTER * 2,
      );
      drawRow({ canvas, image, track, row, rowStartY });
      tileCount += await cutRowIntoTiles({
        canvas, level: row.level, rowIndex, id, createCanvas, encode, files,
      });
      releaseCanvas(canvas);
      done += 1;
    }
  }

  const base = createCanvas(
    Math.max(1, Math.ceil(track.width * BASE_SCALE)),
    Math.max(1, Math.ceil(track.height * BASE_SCALE)),
  );
  const baseCtx = base.getContext('2d');
  baseCtx.imageSmoothingEnabled = true;
  baseCtx.imageSmoothingQuality = 'high';
  baseCtx.drawImage(image, 0, 0, base.width, base.height);
  await writeBasePicture({ base, track, createCanvas, encode, files });
  releaseCanvas(base);

  return { manifest: tileManifest(track), files, tileCount };
}

/** Draws one tile row: the strip of the picture it covers, plus a texel of its neighbours around it. */
function drawRow({ canvas, image, track, row, rowStartY }) {
  const ctx = canvas.getContext('2d');

  const pixelsPerUnitY = image.height / track.height;
  const gutterWorld = TILE_GUTTER / row.scale;
  const contentHeight = Math.min(row.rowWorld, track.height - rowStartY);
  const contentWidth = track.width * row.scale;
  // A picture already at this level's density is copied texel for texel, not resampled: what the
  // caller painted is what the tile holds.
  const sameDensity = contentWidth === image.width
    && Math.abs(contentHeight * row.scale - contentHeight * pixelsPerUnitY) < 1e-6;
  ctx.imageSmoothingEnabled = !sameDensity;
  ctx.imageSmoothingQuality = 'high';

  blit(ctx, image, {
    sx: 0,
    sy: rowStartY * pixelsPerUnitY,
    sw: image.width,
    sh: contentHeight * pixelsPerUnitY,
    dx: TILE_GUTTER,
    dy: TILE_GUTTER,
    dw: contentWidth,
    dh: contentHeight * row.scale,
  });

  // The gutter above and below comes from the neighbouring rows of the picture; at the map's own top
  // and bottom it is off the picture, and the row keeps it transparent, exactly as a painted one does.
  for (const [gutterY, fromY] of [
    [0, rowStartY - gutterWorld],
    [TILE_GUTTER + contentHeight * row.scale, rowStartY + contentHeight],
  ]) {
    blit(ctx, image, {
      sx: 0,
      sy: fromY * pixelsPerUnitY,
      sw: image.width,
      sh: gutterWorld * pixelsPerUnitY,
      dx: TILE_GUTTER,
      dy: gutterY,
      dw: contentWidth,
      dh: TILE_GUTTER,
    });
  }
}

/** drawImage with the source rectangle clamped to the picture, the destination following it. */
function blit(ctx, image, { sx, sy, sw, sh, dx, dy, dw, dh }) {
  const left = Math.max(0, sx);
  const top = Math.max(0, sy);
  const right = Math.min(image.width, sx + sw);
  const bottom = Math.min(image.height, sy + sh);
  if (right <= left || bottom <= top) return;

  const scaleX = dw / sw;
  const scaleY = dh / sh;
  ctx.drawImage(
    image,
    left, top, right - left, bottom - top,
    dx + (left - sx) * scaleX, dy + (top - sy) * scaleY,
    (right - left) * scaleX, (bottom - top) * scaleY,
  );
}
